import dayjs from 'dayjs'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  ContactRound,
  FileBadge2,
  FileText,
  IdCard,
  SquareUserRound,
  TriangleAlert,
} from '@/components/icons'
import { DocumentSubType } from '@@api/shared'

export type DocumentExpiryStatus = 'expired' | 'expiring' | 'valid'

/** Icon for a document sub-type. Covers the crew and aircraft sub-type sets. */
export function getDocumentIcon(type: DocumentSubType) {
  switch (type) {
    case DocumentSubType.Passport:
      return IdCard
    case DocumentSubType.Visa:
    case DocumentSubType.License:
      return FileBadge2
    case DocumentSubType.CrewID:
      return SquareUserRound
    default:
      return FileText
  }
}

/**
 * Icon for a passenger document sub-type. Passengers have their own set
 * (Passport / Visa / TravelID / Other), so TravelID gets its own glyph rather
 * than falling through to the generic FileText above.
 */
export function getPassengerDocumentIcon(type: string) {
  switch (type) {
    case DocumentSubType.Passport:
      return IdCard
    case DocumentSubType.Visa:
      return FileBadge2
    case DocumentSubType.TravelID:
      return ContactRound
    default:
      return FileText
  }
}

/** Expiry status of a single document — "expiring" means within two months. */
export function getDocumentExpiryStatus(expiryDate: Date | string): DocumentExpiryStatus {
  const now = dayjs()
  const expiry = dayjs(expiryDate)
  if (expiry.isBefore(now)) return 'expired'
  if (expiry.isBefore(now.add(2, 'month'))) return 'expiring'
  return 'valid'
}

interface DocumentListCardProps {
  icon: React.ReactNode
  title: string
  subTypeLabel: string
  expiryLabel: string
  expiryStatus?: DocumentExpiryStatus
  /** Renders a Public/Private visibility badge when provided. */
  isPublic?: boolean
  onClick?: () => void
}

export function DocumentListCard({
  icon,
  title,
  subTypeLabel,
  expiryLabel,
  expiryStatus,
  isPublic,
  onClick,
}: DocumentListCardProps) {
  return (
    <Card
      interactive={!!onClick}
      className="p-4 cursor-pointer hover:bg-surface-L2 transition-colors"
      onClick={onClick}
    >
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-4">
          <span className="shrink-0 text-content-brand-bold [&>svg]:size-6">
            {icon}
          </span>
          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium tracking-[0.07px]">{title}</p>
            <div className="flex flex-wrap gap-3">
              <Badge variant="outline" className="rounded-lg text-xs font-medium tracking-[0.18px]">
                {subTypeLabel}
              </Badge>
              <Badge variant="outline" className="rounded-lg text-xs font-medium tracking-[0.18px]">
                {expiryLabel}
              </Badge>
              {isPublic !== undefined && (
                <Badge variant="outline" className="rounded-lg text-xs font-medium tracking-[0.18px]">
                  {isPublic ? 'Public' : 'Private'}
                </Badge>
              )}
            </div>
          </div>
        </div>
        <span className="shrink-0">
          {expiryStatus === 'expiring' && (
            <TriangleAlert className="size-4 text-background-notice-bold" />
          )}
          {expiryStatus === 'expired' && (
            <TriangleAlert className="size-4 text-content-negative-bold" />
          )}
        </span>
      </div>
    </Card>
  )
}
